import React from 'react';
import { withRouter } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import {
    Modal,
    ModalHeader,
    ModalBody,
    Button,
    Table,
    FormGroup,
    Col,
    ModalFooter,
    UncontrolledTooltip,
    Spinner,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faPlus, faTimes, faArrowRightFromBracket, faPaste } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuid } from 'uuid';
import { isEmpty } from 'lodash-es';
import AddressFormNew from '../address/AddressFormNew';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { BaseFormViewModel } from '../common/ViewModel';
import { Job } from '../job/Job';
import { util } from '../Util';
import LocationSelector from './LocationSelector';
import CommonContext, { ApiRoutes, ChargeTypeResourceTypes, RentalUnitTypes, SupportedFileExtensions } from '../Common';
import { FormCheckbox, FormDivider, FormGroupColumn, FormLabel, GroupedRow, IconButton, onFieldChange, onReactSelectChanged, SmallButton, toasty } from '../common/forms/FormElements';
import FileUpload from '../common/forms/FileUpload';
import './JobForm.scss';
import moment from 'moment';
import Input from 'components/common/forms/Input';
import NumericInput from 'components/common/forms/NumericInput';
import { Textarea } from 'components/common/forms/Textarea';

const defaultEdit = {
    dayStatuses: {},
    resourceErrors: {},
    initialResources: null,
    allowRequestedBy: true,
    allowWeekOfDate: true,
    allowForemen: true,
    allowDispatchOfficeLocation: true,
    allowJobType: true,
    allowStartTime: true, // or false if that’s your intended default
    allowWorkOrderNumber: true,
    allowResources: true,
    allowMeetingLocation: true,
    allowJobLocation: true,
    allowNotes: true,
    allowDelete: false,
    allowSubcontractor: true,
    allowProjectManagers: true,
    allowInspectorName: true,
};

class JobForm extends React.Component {
    static contextType = CommonContext;
    constructor(props) {
        super(props);

        this.state = {
            job: new Job(),
            usCounties: [],
            customerSubcontractors: [],
            tableRequired: {},
            formState: new BaseFormViewModel(),
            edit: defaultEdit,
            // For location form
            selectedAddressType: null,
            // For toggling modals
            showAddSetupCharge: false,
            showAddCertification: false,
            showAddPermitFees: false,
            uploadSessionId: null,
        };

        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onSaveJob = this.onSaveJob.bind(this);
        this.addressFormRef = React.createRef();
        this.formRef = React.createRef();
    }
    onSelectChanged = onReactSelectChanged;
    onChange = onFieldChange;

    onAddCertification = () => {
        this.setState({ showAddCertification: false });
    };

    removeCertification = (resource) => {
        const { selectedDate } = this.props;
        const { job } = this.state;
        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' })
            .toLowerCase();

        job.resources = job.resources.map(r =>
            r.id === resource.id
                ? { ...r, isNew: false, [dayKey]: 0 }
                : r
        );

        this.setState({ job });
    };

    onAddPermitFees = () => {
        const { job } = this.state;
        const { selectedDate } = this.props;
        const dayKey = selectedDate
            .toLocaleString('en-us', { weekday: 'long' })
            .toLowerCase();

        // Re-hydrate the job to assign quantity=1 if flaggers > 0
        const updatedJob = this.hydrateSetupCharges(job, dayKey);

        this.setState({
            job: updatedJob,
            showAddPermitFees: false
        });
    };

    onAddSetupCharge = () => {
        const { job } = this.state;
        const { selectedDate } = this.props;
        const dayKey = selectedDate
            .toLocaleString('en-us', { weekday: 'long' })
            .toLowerCase();

        // Re-hydrate the job to assign quantity=1 if flaggers > 0
        const updatedJob = this.hydrateSetupCharges(job, dayKey);

        this.setState({
            job: updatedJob,
            showAddSetupCharge: false
        });
    };

    resetForm = () => {
        const newJob = new Job();
        this.setState({
            formState: { ...this.state.formState, formValidated: false, errors: {}, saving: false },
            tableRequired: {},
            uploadSessionId: null,
            job: newJob,
        });
    };

    componentDidMount() {
        if (this.props.slideOpen) {
            this.populateState();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.slideOpen && !prevProps.slideOpen) {
            void this.populateState();
        }
    }

    populateState = async () => {
        const { selectedJob, project, contract, dispatchLocations, customerSubcontractors, contractResources } = this.props;
        const { formState } = this.state;

        try {
            this.setState({ formState: { ...formState, loading: true } });

            let fetchedJob;
            let jobManage = {};

            if (selectedJob && selectedJob.id && !selectedJob.isNew) {
                jobManage = await util.fetch.js(ApiRoutes.job.byId(selectedJob.id));
                fetchedJob = selectedJob.isUpdated ? selectedJob : jobManage.job;

                fetchedJob.resources = fetchedJob.resources.map((resource) => {
                    const rentalContractResource = contractResources.find((cr) =>
                        !!cr.equipmentTypeId
                        && cr.equipmentTypeId === resource.equipmentTypeId
                        && cr.jobResourceTypeId === resource.jobResourceTypeId
                        && cr.jobResourceTypeId === ChargeTypeResourceTypes.Rental
                    );

                    return {
                        ...resource,
                        rentalUnitTypeId: rentalContractResource?.rentalUnitTypeId ?? null,
                    };
                });
            } else if (selectedJob && selectedJob.tempSessionId) {
                fetchedJob = selectedJob;
                // Normalize startTime to HH:mm if it’s an ISO string, or use default
                if (fetchedJob.startTime && fetchedJob.startTime.includes('T')) {
                    const date = new Date(fetchedJob.startTime);
                    fetchedJob.startTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                } else {
                    fetchedJob.startTime = fetchedJob.startTime || '';
                }
            } else {
                // New job scenario, inherit from project
                fetchedJob = selectedJob || new Job();
                fetchedJob.isNew = true;
                fetchedJob.tempSessionId = `temp-${uuid()}`;

                if (project && project.contractId) {
                    fetchedJob.contract = contract;
                    fetchedJob.contractId = contract.id;
                    fetchedJob.description = contract.description;
                    fetchedJob.resources = (contractResources || []).map(r => ({
                        ...r,
                        id: r.id || uuid()
                    }));
                    // Inherit additional fields from project
                    fetchedJob.foremanId = 0;
                    fetchedJob.foremenId = project.foremanId || null;
                    fetchedJob.requestedById = project.requestedById || null;
                    fetchedJob.projectManagerId = project.projectManagerId || null;
                    fetchedJob.projectName = project.projectName || null;
                    fetchedJob.workOrderNumber = project.workOrderNumber || '';
                    fetchedJob.dispatchOfficeLocationId = project.dispatchCompanyId || null;
                    fetchedJob.meetingLocation = project.meetingLocation || null;
                    fetchedJob.jobLocation = project.jobLocation || null;
                    fetchedJob.notes = project.notes || null;
                    fetchedJob.inspectorName = project.inspectorName || '';
                    fetchedJob.startTime = project.startTime || '';
                    fetchedJob.jobTypeId = project.jobTypeId || null;
                }
                if (dispatchLocations && dispatchLocations.length === 1) {
                    fetchedJob.dispatchOfficeLocationId = dispatchLocations[0].value;
                }
            }

            this.setState({
                customerSubcontractors,
                job: fetchedJob,
                uploadSessionId: fetchedJob.tempSessionId,
                edit: {
                    ...defaultEdit,
                    ...jobManage.edit,
                    initialResources: fetchedJob.resources ? { ...fetchedJob.resources } : null,
                },
                formState: { ...formState, loading: false },
            });
        } catch (error) {
            console.error('Failed to fetch job data:', error);
            toasty.error('Error loading job data');
            this.setState({ formState: { ...formState, loading: false } });
        }
    };


    ////////// RESOURCE-RELATED METHODS (INLINE) //////////

    // Check if an AFAD resource has any assignment for the given day.
    doesAfadAssignmentExistForDay(job, dayName) {
        if (!job?.resources) return false;
        return job.resources.some(r => r.isAFAD && Number(r[dayName]) > 0);
    }

    // Extracts the base resource name for planning purposes.
    getNonPlanningResourceName = (resourceName) => {
        return resourceName.startsWith('Flagger')
            ? resourceName.split(' ')[0]
            : null;
    };

    // Check if a resource has any assignment on any day.
    resourceHasAnyAssignments = (resource) =>
        Boolean(resource && ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            .some(day => Number(resource[day]) > 0));


    // Check if a resource is flagged as an AFAD resource.
    resourceIsAfad = (resource) => Boolean(
        resource && resource.isAFAD,
    );

    // Returns the start date (ISO string) of the week for a given date.
    getWeekStartDate(date) {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - (day === 0 ? 6 : day - 1);
        const startOfWeek = new Date(d.setDate(diff));
        return startOfWeek.toISOString().split('T')[0];
    }

    getResourceStatus = (dayKey, resourceName) => {
        const { edit } = { ...this.state };
        return edit?.dayStatuses?.[dayKey]?.resourceStatuses?.[resourceName] || null;
    };

    getPairedResourceCount = (resourceName, dayKey) => {
        const { job } = this.state;
        const tenantSettings = this.context.tenant.tenantSettings;
        if (!tenantSettings || !tenantSettings.enableRequestedPersonnel) return null;

        // Get the base resource name (e.g., "Flaggers" from "Flaggers (Planning)")
        const baseName = this.getNonPlanningResourceName(resourceName);
        if (!baseName) return null;

        // Find an alternate resource whose name starts with the baseName but is not the same resource.
        const alternateResource = job.resources.find(
            (r) => r.resourceName !== resourceName && r.resourceName.startsWith(baseName)
        );
        if (!alternateResource) return null;

        return alternateResource[dayKey] || 0;
    };

    getResourceEditValidationMessage = (resourceName, newVal) => {
        const { selectedDate } = this.props;
        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();

        let msg = null;
        // Get resource status details for this day and resource.
        const resourceStatus = this.getResourceStatus(dayKey, resourceName);
        if (resourceStatus?.statusCounts) {
            const maxStatus = resourceStatus.maxConfirmationStatusId;
            if (maxStatus < 2) return null; // No restrictions if maxStatus < 2

            // Determine a threshold based on existing assignments (status 2-4).
            const minCheckStatus = maxStatus === 2 ? 2 : 1;
            const threshold = Object.keys(resourceStatus.statusCounts).reduce((acc, statKey) => {
                const sKeyNum = parseInt(statKey, 10);
                if (sKeyNum >= minCheckStatus && sKeyNum <= 4) {
                    acc += resourceStatus.statusCounts[statKey];
                }
                return acc;
            }, 0);

            if (newVal < threshold) {
                const uppercaseDay = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
                msg = `"${resourceName}" must be at least ${threshold} on ${uppercaseDay} due to existing statuses.`;
                return msg;
            }
        }

        return msg;
    };

    // Updates the resource assignments for a specific day.
    updateJobResourcesForDay(resource, newQuantity) {
        const { selectedDate } = this.props;
        const { job, edit, showAddSetupCharge } = this.state;

        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();

        let val = parseInt(newQuantity, 10);
        if (isNaN(val)) val = 0;
        if (val >= 100) {
            toasty.error('Resource assignment cannot exceed 100.');
            return job;
        }


        // Validate new quantity against current assignments and thresholds.
        const errorMsg = this.getResourceEditValidationMessage(resource.resourceName, val);

        if (errorMsg) {
            toasty.error('Resource Update', errorMsg);
            // Save the error message in the edit object's resourceErrors.
            const npName = this.getNonPlanningResourceName(resource.resourceName) || resource.resourceName;
            edit.resourceErrors[`${npName}_${dayKey}`] = errorMsg;
            return job;
        } else {
            // Clear any previous errors for this resource.
            const npName = this.getNonPlanningResourceName(resource.resourceName) || resource.resourceName;
            delete edit.resourceErrors[`${npName}_${dayKey}`];
        }

        // Update the specific resource's assignment for the day.
        let updatedJob = {
            ...job,
            resources: job.resources.map((r) =>
                r.id === resource.id ? { ...r, [dayKey]: val } : r
            )
        };

        // For planning-only resources: if no AFAD assignment exists, reset their value.
        updatedJob = {
            ...updatedJob,
            resources: updatedJob.resources.map((r) =>
                r.isPlanningOnly && !this.doesAfadAssignmentExistForDay(updatedJob, dayKey)
                    ? { ...r, [dayKey]: 0 }
                    : r
            )
        };

        // If setup charges should be hydrated, update the job.
        if (showAddSetupCharge) {
            updatedJob = this.hydrateSetupCharges(updatedJob, dayKey);
        }


        return updatedJob;
    }

    // Hydrates setup charges for a specific day.
    hydrateSetupCharges = (job, dayKey) => {
        if (!job?.resources?.length) return job;
        const flaggers = job.resources.find(
            (r) => !r.isPlanningOnly && r.resourceName === 'Flaggers'
        );
        if (!flaggers) return job;
        const hasFlaggers = this.resourceHasAnyAssignments(flaggers);
        const resources = job.resources.map((r) =>
            r.setupChargeTypeId > 0 && r.isNew ? { ...r, [dayKey]: hasFlaggers ? 1 : 0 } : r
        );
        return { ...job, resources };
    };

    removeSetupCharge = (resource) => {
        const { selectedDate } = this.props;
        const { job } = this.state;
        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();

        // Update the job resources
        const updatedResources = job.resources.map(r =>
            r.id === resource.id ? { ...r, isNew: false, [dayKey]: 0 } : r
        );

        // Rehydrate setup charges after removal
        const updatedJob = this.hydrateSetupCharges({ ...job, resources: updatedResources }, dayKey);

        this.setState({ job: updatedJob });
    };

    handleQuantityChange = (resourceId, newQuantity) => {
        const { selectedDate } = this.props;
        const { job } = this.state;

        if (!selectedDate) return;

        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();
        const resource = job.resources.find(r => r.id === resourceId);
        if (!resource) return;

        let updatedJob = this.updateJobResourcesForDay(resource, newQuantity);

        if (resource.resourceName === 'Flaggers') {
            updatedJob = this.hydrateSetupCharges(updatedJob, dayKey);
        }

        this.setState({ job: updatedJob });
    };

    handlePickupRemainingEquipment = (resourceId) => {
        const { job } = this.state;
        const { project } = this.props;

        const resource = job.resources.find(r => r.id === resourceId);

        const projectResource = project.resources.find(r => r.equipmentTypeId === resource.equipmentTypeId);

        this.handleRentalQuantityChange(resourceId, 'rentalPickupQuantity', projectResource?.actualQuantity || projectResource?.expectedQuantity || 0);
    };

    handleRentalQuantityChange = (resourceId, field, newQuantity) => {
        const { selectedDate } = this.props;

        if (!selectedDate) return;

        // 1) get the date you want to default to when qty goes > 0
        const defaultDate = this.getDefaultRentalDate();

        // 2) normalize the incoming quantity to a number
        const quantity = parseFloat(newQuantity) || 0;
  
        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();

        const { job } = this.state;
        const resource = job.resources.find(r => r.id === resourceId);

        if (!resource) return; // Ensure resource exists before proceeding

        // Update the specific resource's assignment for the day.
        const updatedJob = {
            ...job,
            resources: job.resources.map((r) => {
                if (r.id === resource.id) {
                    let dayQuantity = parseFloat(resource[dayKey] || (newQuantity > 0 ? 1 : 0));

                    if (!resource.equipmentTypeIsGeneric) {
                        dayQuantity = parseFloat(newQuantity);

                        // The total quantity of the day should equal both dropoffs AND pickups
                        if (field === 'rentalDropoffQuantity') {
                            dayQuantity += parseFloat(resource.rentalPickupQuantity || 0);
                        } else if (field === 'rentalPickupQuantity') {
                            dayQuantity += parseFloat(resource.rentalDropoffQuantity || 0);
                        }

                    }
                    
                    const updatedResource = {
                        ...r,
                        [field]: quantity,
                        [dayKey]: dayQuantity,
                    };

                    if (field === 'rentalDropoffQuantity') {
                        updatedResource.rentalStartDate = quantity > 0
                            ? (r.rentalStartDate || defaultDate)
                            : null;
                    } else if (field === 'rentalPickupQuantity') {
                        updatedResource.rentalEndDate = quantity > 0
                            ? (r.rentalEndDate || defaultDate)
                            : null;
                    }

                    return updatedResource;
                    
                }

                return r;
            }),
        };

        this.setState({ job: updatedJob });
    };

    handleRentalDateChange = (resourceId, field, date) => {
        const { job } = this.state;
        const resource = job.resources.find(r => r.id === resourceId);

        if (!resource) return; // Ensure resource exists before proceeding

        // Update the specific resource's assignment for the day.
        const updatedJob = {
            ...job,
            resources: job.resources.map((r) =>
                r.id === resource.id ? { ...r, [field]: date } : r
            ),
        };

        this.setState({ job: updatedJob });
    };

    applySameRentalStartDateToAll = () => {
        const { job } = this.state;
        const { resources } = job;

        const firstResourceWithStartDate = resources.find(r => r.rentalDropoffQuantity > 0 && r.rentalStartDate !== null);

        if (firstResourceWithStartDate) {
            const { rentalStartDate } = firstResourceWithStartDate;

            // Update the specific resource's assignment for the day.
            const updatedJob = {
                ...job,
                resources: job.resources.map((r) => ({
                    ...r,
                    rentalStartDate: r.rentalDropoffQuantity > 0 ? rentalStartDate : null,
                })),
            };

            this.setState({ job: updatedJob });
        }
    };

    applySameRentalEndDateToAll = () => {
        const { job } = this.state;
        const { resources } = job;

        const firstResourceWithEndDate = resources.find(r => r.rentalPickupQuantity > 0 && r.rentalEndDate !== null);

        if (firstResourceWithEndDate) {
            const { rentalEndDate } = firstResourceWithEndDate;

            // Update the specific resource's assignment for the day.
            const updatedJob = {
                ...job,
                resources: job.resources.map((r) => ({
                    ...r,
                    rentalEndDate: r.rentalPickupQuantity > 0 ? rentalEndDate : null,
                })),
            };

            this.setState({ job: updatedJob });
        }
    };

    onSaveJob = async (event) => {
        // Prevent the default form submission behavior.
        event.preventDefault();

        // Use the native form validation
        if (!this.formRef.current.checkValidity()) {
            // This will display the browser's built-in validation errors.
            this.formRef.current.reportValidity();
            return;
        }

        const { job } = this.state;
        const { selectedDate, project, onJobSave, refreshProject } = this.props;
        if (!job) return;
        if (!selectedDate) {
            toasty.error('No selected date!');
            return;
        }
        if (!this.onValidate()) {
            toasty.error('Validation failed. Please fix the errors.');
            return;
        }
        let updatedJob = { ...job };
        if (!updatedJob.id) {
            updatedJob.weekOfDate = this.getWeekStartDate(selectedDate);
            updatedJob.projectId = project.id;
            // Combine selectedDate and startTime into jobDate
            if (updatedJob.startTime) {
                const [hours, minutes] = updatedJob.startTime.split(':').map(Number);
                const jobDateTime = new Date(selectedDate);
                jobDateTime.setHours(hours, minutes, 0, 0);
                updatedJob.jobDate = jobDateTime.toISOString();
            }
        } else {
            updatedJob.tempSessionId = updatedJob.id ? null : updatedJob.tempSessionId;
            updatedJob.isNew = false;
            updatedJob.isUpdated = true;
            updatedJob.weekOfDate = updatedJob.weekOfDate || this.getWeekStartDate(selectedDate);
        }

        // Clean up job locations (if the id is a string, we assume it's temporary and set it to null)
        updatedJob.meetingLocation = typeof updatedJob.meetingLocation?.id === 'string'
            ? { ...updatedJob.meetingLocation, id: null }
            : updatedJob.meetingLocation;
        updatedJob.jobLocation = typeof updatedJob.jobLocation?.id === 'string'
            ? { ...updatedJob.jobLocation, id: null }
            : updatedJob.jobLocation;

        // Clean up resources: if an id is a string, set it to null so that the backend creates a new resource record.
        updatedJob.resources = updatedJob.resources.map((r) => ({
            ...r,
            id: typeof r.id === 'string' ? null : r.id,
            rentalDropoffDate: r.rentalDropoffQuantity > 0 ? selectedDate : null,
            rentalPickupDate: r.rentalPickupQuantity > 0 ? selectedDate : null,
        }));

        // Clean up start time: combine selectedDate with startTime and convert to ISO string.
        if (updatedJob.startTime) {
            const [hours, minutes] = updatedJob.startTime.split(':').map(Number);
            const jobDate = new Date(updatedJob.jobDate || selectedDate);
            jobDate.setHours(hours, minutes, 0, 0);
            updatedJob.startTime = jobDate.toISOString();
        }

        // If a project already exists, call the API immediately.
        if (project.id) {
            try {
                let response;
                if (updatedJob.isNew || !updatedJob.id) {
                    // Create a new job directly
                    response = await util.fetch.post(ApiRoutes.job.create(), updatedJob, util.fetch.format.json);
                } else {
                    // Update existing job directly
                    response = await util.fetch.put(ApiRoutes.job.update(updatedJob.id), updatedJob, util.fetch.format.json);
                }

                if (response.error === 0) {
                    toasty.success(`Job ${updatedJob.isNew ? 'added' : 'updated'} successfully!`);
                    if (refreshProject) {
                        await refreshProject();
                    }
                } else {
                    throw new Error(response.message || 'Failed to save job.');
                }
            } catch (error) {
                console.error('Error saving job:', error);
                toasty.error('Failed to save job.');
            }
        } else {
            // For a new project (no project.id yet), pass the job back to the parent and let it handle saving later.
            onJobSave(updatedJob);
        }
        this.resetForm();
        this.props.setSlideOpen(false);
    };

    onValidate = () => {
        let valid = true;
        let errors = {};
        const requiredStyle = {
            border: '1px solid',
            borderRadius: '0.25rem',
            borderColor: '#ed1c24'
        };

        const hasAssignedResources = this.state.job.resources?.some(resource =>
            this.resourceHasAnyAssignments(resource)
        );

        if (!hasAssignedResources) {
            valid = false;
            errors.resources = 'At least one resource with a non-zero quantity is required.';
            this.setState({ tableRequired: requiredStyle });
        }

        if (this.state.job.contract?.setupChargesRequired) {
            const hasSetupCharge = this.state.job.resources
                ?.filter(r => r.setupChargeTypeId > 0)
                .some(resource => this.resourceHasAnyAssignments(resource));
            if (!hasSetupCharge) {
                valid = false;
                errors.setupCharge = 'At least one setup charge is required.';
            }
        }

        if (!this.state.job.meetingLocation) {
            valid = false;
            errors.meetingLocation = 'Meeting Location is required.';
        }
        if (!this.state.job.jobLocation) {
            valid = false;
            errors.jobLocation = 'Job Location is required.';
        }
        if (!this.state.job.startTime) {
            valid = false;
            errors.startTime = 'Start Time is required.';
        }

        if (this.state.job.contract.isProjectContract) {
            const missingDates = this.state.job.resources
                .filter(r => r.jobResourceTypeId === ChargeTypeResourceTypes.Rental)
                .filter(r => {
                    const dropoff = (r.rentalDropoffQuantity || 0) > 0;
                    const pickup  = (r.rentalPickupQuantity || 0) > 0;
                    return (dropoff && !r.rentalStartDate) || (pickup  && !r.rentalEndDate);
                });
            
            if (missingDates.length > 0) {
                valid = false;
                errors.resources = 'Please enter a rental start or rental end date for every allocated rental resource.';
                this.setState({ tableRequired: requiredStyle });
            }
        }

        this.setState({ formState: { ...this.state.formState, formValidated: valid, errors: errors, saving: valid } });
        return valid;
    };

    onSaveAddress = (address) => {
        const { selectedAddressType, job } = this.state;
        const { usCounties } = this.props;

        if (!selectedAddressType) return;

        const countyData = util.getCounty(usCounties, address);
        const updatedJob = {
            ...job,
            [selectedAddressType]: { ...address, county: countyData }
        };

        if (address.duplicate) {
            if (selectedAddressType === 'jobLocation' && !updatedJob.meetingLocation) {
                updatedJob.meetingLocation = { ...address, id: uuid(), county: countyData };
            } else if (selectedAddressType === 'meetingLocation' && !updatedJob.jobLocation) {
                updatedJob.jobLocation = { ...address, id: uuid(), county: countyData };
            }
        }

        this.setState({ job: updatedJob, selectedAddressType: null });
    };

    handlePermitFeesApplyChange = () => {
        const { job } = this.state;
        job.permitFeesApply = !job.permitFeesApply;

        this.setState({ job });
    };

    handleCloseModal = () => {
        this.resetForm();
        this.props.setSlideOpen(false);
    };

    isRequestedPersonnelSectionAvailable() {
        const { job } = this.state;
        return (
            job?.resources &&
            job?.resources.some((r) => r.isPlanningOnly) &&
            job?.resources.some((r) => this.resourceIsAfad(r) && this.resourceHasAnyAssignments(r))
        );
    }

    /* File uploads */
    onRemoveAttachment = async (file) => {
        const { job } = { ...this.state };
        const url = ApiRoutes.job.attachment(job.id || this.state.uploadSessionId);
        const updatedUploads = job.uploads.filter(f => f.name !== file.name);

        this.setState({
            job: {
                ...job,
                uploads: updatedUploads,
            },
        });

        try {
            const response = await util.fetch.delete(url, file, util.fetch.format.json);

            if (response) {
                toasty.success('Attachment removed.');
                // Fetch updated list to confirm the removal
                this.getAttachments();
            } else {
                const errorData = await response.json();
                toasty.error(errorData.message || 'Failed to remove the attachment. Try refreshing.');
            }
        } catch (error) {
            toasty.error('Could not delete attachment due to a server error.');
        }
    };

    onDownloadAttachment = async (file) => {
        const { job } = { ...this.state };
        if (!file) return;

        const url = ApiRoutes.job.attachment(job.id || this.state.uploadSessionId);
        util.fetch.downloadFile(url, file, file.name);
    };

    getAttachments = async () => {
        const { job } = { ...this.state };
        const jobIdOrSessionId = job.id ? job.id : this.state.uploadSessionId;
        const attachments = await util.fetch.get(
            ApiRoutes.job.getAllAttachments(jobIdOrSessionId),
        );

        this.setState({
            job: {
                ...job,
                uploads: attachments ?? [],
            },
        });
    };

    getDefaultRentalDate() {
        const { selectedDate } = this.props;
        const { job: { startTime } } = this.state;
        const m = moment(selectedDate).clone();
      
        if (startTime) {
            const [hours, minutes] = startTime.split(':').map(Number);
            if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
                m.set({ hour: hours, minute: minutes });
            } else {
                const parsed = moment(startTime);
                if (parsed.isValid()) {
                    m.set({ hour: parsed.hour(), minute: parsed.minute() });
                }
            }
        } else {
            m.startOf('day');
        }
      
        return m.toDate();
    }

    render() {
        const {
            formState, job, tableRequired, edit,
            showAddSetupCharge, showAddCertification, showAddPermitFees, customerSubcontractors,
        } = this.state;

        const {
            slideOpen, selectedDate, contactsOptions, dispatchLocations,
            selectedJob, onAddContact, onEditContact, project,
            usCounties, contract, jobTypesOptions
        } = this.props;

        const tenantSettings = this.context.tenant.tenantSettings;

        const unusedCertifications = (job?.resources || []).filter(r => r.certificationId > 0 && !r.isPermit);
        const unusedSetupChargeTypes = (job?.resources || []).filter(r => r.setupChargeTypeId > 0 && !r.isPermit);
        const unusedPermitFees = (job?.resources || []).filter(r => r.setupChargeTypeId > 0 && r.isPermit);

        const supportedFiles = SupportedFileExtensions.image
            .concat(SupportedFileExtensions.document)
            .join(',');

        return (
            <React.Fragment>
                <Modal isOpen={slideOpen} toggle={this.handleCloseModal} size="lg">
                    <ModalHeader toggle={this.handleCloseModal}>
                        {selectedJob?.id ? 'Edit Job' : 'Create Job'} {' '}
                        {selectedDate && job?.contractSequence ? `(${job.jobNumber}) on ${selectedDate.toLocaleDateString()}` : `${selectedDate?.toLocaleDateString()}`}
                    </ModalHeader>
                    <ModalBody>
                        {formState.loading || !tenantSettings ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                                <Spinner />
                            </div>
                        ) : (
                            <form ref={this.formRef} onSubmit={this.onSaveJob} noValidate>
                                <GroupedRow>
                                    <FormGroupColumn>
                                        <FormGroup className="relative">
                                            <FormLabel htmlFor="requestedById" text="Requested By" required />
                                            {!job?.hasAssignments && (
                                                <SmallButton
                                                    color="outline-primary"
                                                    className="floating ml-auto mr-0"
                                                    onClick={() => onAddContact('requestedById')}
                                                >
                                                    <FontAwesomeIcon icon={faPlusCircle} size="md" className="mr-2" />
                                                    Add Contact
                                                </SmallButton>
                                            )}
                                            <ValidatedSelect
                                                id="requestedById"
                                                name="job.requestedById"
                                                options={contactsOptions?.companyContactsOptions}
                                                value={(contactsOptions?.companyContactsOptions || []).find(
                                                    (x) => x.value === job?.requestedById
                                                ) || ''}
                                                onChange={this.onSelectChanged}
                                                required
                                                validationMessage="A requested-by selection is required."
                                                isDisabled={!edit.allowRequestedBy}
                                            />
                                            {job?.requestedById && (
                                                <SmallButton
                                                    color="link"
                                                    className="site-link w-auto text-right"
                                                    onClick={() => onEditContact('requestedById')}
                                                >
                                                    Edit Requestor Details
                                                </SmallButton>
                                            )}
                                        </FormGroup>
                                    </FormGroupColumn>

                                    <FormGroupColumn>
                                        <FormGroup>
                                            <FormLabel htmlFor="dispatchOfficeLocationId" text="Dispatching" required />
                                            <ValidatedSelect
                                                id="dispatchOfficeLocationId"
                                                name="job.dispatchOfficeLocationId"
                                                options={dispatchLocations || []}
                                                value={(dispatchLocations || []).find(
                                                    (d) => d.value === job?.dispatchOfficeLocationId
                                                ) || ''}
                                                onChange={this.onSelectChanged}
                                                required
                                                validationMessage="A dispatch selection is required."
                                                isDisabled={!edit.allowDispatchOfficeLocation}
                                            />
                                        </FormGroup>
                                    </FormGroupColumn>
                                </GroupedRow>

                                <GroupedRow>
                                    <FormGroupColumn>
                                        <FormGroup className="relative">
                                            <FormLabel htmlFor="foremenId" text="Foreman" required />
                                            {!job?.hasAssignments && (
                                                <SmallButton
                                                    color="outline-primary"
                                                    className="floating ml-auto mr-0"
                                                    onClick={() => onAddContact('foremenId')}
                                                >
                                                    <FontAwesomeIcon icon={faPlusCircle} size="md" className="mr-2" />
                                                    Add Contact
                                                </SmallButton>
                                            )}
                                            <ValidatedSelect
                                                id="foremenId"
                                                name="job.foremenId"
                                                options={contactsOptions?.companyContactsOptions}
                                                value={(contactsOptions?.companyContactsOptions || []).find(
                                                    (x) => x.value === job?.foremenId
                                                ) || ''}
                                                onChange={this.onSelectChanged}
                                                required
                                                validationMessage="A foreman selection is required."
                                                isDisabled={!edit.allowForemen}
                                            />
                                            {job?.foremenId && (
                                                <SmallButton
                                                    color="link"
                                                    className="site-link w-auto text-right"
                                                    onClick={() => onEditContact('foremanId')}
                                                >
                                                    Edit Foreman Details
                                                </SmallButton>
                                            )}
                                        </FormGroup>
                                    </FormGroupColumn>
                                    {job.contract.workOrderTypeId !== 3 && (
                                        <FormGroupColumn>
                                            <Input
                                                label="Work Order #"
                                                id="workOrderNumber"
                                                name="job.workOrderNumber"
                                                value={
                                                    job.workOrderNumber ?? ''
                                                }
                                                onChange={this.onChange}
                                                readOnly={
                                                    !edit.allowWorkOrderNumber
                                                }
                                                maxLength={44}
                                            />
                                        </FormGroupColumn>
                                    )}
                                </GroupedRow>
                                {(tenantSettings?.contractsCanEnforceProjectManagers && (job.contract ?? {}).requireProjectInformation) && (
                                    <>
                                        <GroupedRow>
                                            <FormGroupColumn>
                                                <Input
                                                    label="Project Name"
                                                    id="projectName"
                                                    name="job.projectName"
                                                    value={job.projectName ?? ''}
                                                    onChange={this.onChange}
                                                    error="Project Name is required."
                                                    required
                                                />
                                            </FormGroupColumn>
                                            <FormGroupColumn>
                                                <FormGroup className="relative">
                                                    <div className="d-flex flex-row flex-nowrap align-items-baseline justify-content-start">
                                                        <FormLabel
                                                            htmlFor="projectManager"
                                                            text="Project Manager"
                                                            required
                                                        />
                                                        <SmallButton
                                                            color="outline-primary"
                                                            className="floating ml-auto mr-0"
                                                            onClick={() => onAddContact(
                                                                'projectManagerId',
                                                            )}
                                                        >
                                                            <FontAwesomeIcon icon={faPlusCircle} size="md" className="mr-2" />
                                                            Add Contact
                                                        </SmallButton>
                                                    </div>
                                                    <ValidatedSelect
                                                        id="projectManager"
                                                        name="job.projectManagerId"
                                                        options={contactsOptions?.projectManagerContactOptions || []}
                                                        value={(contactsOptions?.projectManagerContactOptions || []).find(
                                                            (x) => x.value === job?.projectManagerId
                                                        ) || ''}
                                                        onChange={this.onSelectChanged}
                                                        required
                                                        validationMessage="A Project Manager selection is required."
                                                        isDisabled={!edit.allowProjectManagers}
                                                    />
                                                    {!!job?.projectManagerId && (
                                                        <SmallButton
                                                            color="link"
                                                            className="site-link w-auto text-right"
                                                            onClick={() => onEditContact(
                                                                'projectManagerId',
                                                            )}
                                                        >
                                                            Edit Project Manager Details
                                                        </SmallButton>
                                                    )}
                                                </FormGroup>
                                            </FormGroupColumn>
                                        </GroupedRow>
                                    </>
                                )}

                                <GroupedRow>
                                    {job?.contract?.invoiceHasInspectorName && (
                                        <FormGroupColumn>
                                            <Input
                                                id="inspectorName"
                                                name="job.inspectorName"
                                                value={
                                                    job.inspectorName ?? ''
                                                }
                                                onChange={this.onChange}
                                                readOnly={
                                                    !edit.allowInspectorName
                                                }
                                            />
                                        </FormGroupColumn>
                                    )}
                                    {job?.contract?.subcontractorsApply && (
                                        <FormGroupColumn>
                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="subcontractorCompanyId"
                                                    text="Subcontractor"
                                                    required={
                                                        !!job.contract
                                                            .subcontractorsApply
                                                    }
                                                />
                                                <ValidatedSelect
                                                    id="subcontractorCompanyId"
                                                    name="job.subcontractorCompanyId"
                                                    options={customerSubcontractors || []}
                                                    value={(customerSubcontractors || []).find(
                                                        (x) => x.value === job?.subcontractorCompanyId
                                                    ) || ''}
                                                    onChange={this.onSelectChanged}
                                                    required
                                                    validationMessage="A subcontractor selection is required."
                                                />
                                            </FormGroup>
                                        </FormGroupColumn>
                                    )}

                                </GroupedRow>
                                <FormDivider />
                                <GroupedRow>
                                    <FormGroupColumn>
                                        <Input
                                            label="Start Time"
                                            id="startTime"
                                            name="job.startTime"
                                            type="time"
                                            value={job?.startTime || ''}
                                            onChange={this.onChange}
                                            required
                                            disabled={!edit.allowStartTime}
                                            error={formState.errors.startTime}
                                        />
                                    </FormGroupColumn>
                                    
                                    {job?.jobTypeId !== 1 && (
                                        <FormGroupColumn>
                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="jobType"       
                                                    text="What type of work is being done?"
                                                    required
                                                />
                                                <ValidatedSelect
                                                    id="jobType"
                                                    name="job.jobTypeId"
                                                    options={jobTypesOptions}
                                                    value={
                                                        (jobTypesOptions ?? []).find(
                                                            (x) => x.value === job.jobTypeId,
                                                        ) ?? ''
                                                    }
                                                    onChange={this.onSelectChanged}
                                                    required
                                                    validationMessage="A job type selection is required."
                                                />
                                            </FormGroup>
                                        </FormGroupColumn>
                                    )}
                                </GroupedRow>
                                {/* Resources Table */}
                                <GroupedRow>
                                    <Col>
                                        <FormGroup>
                                            <FormLabel htmlFor="resources" text="Resources" />
                                            <div style={tableRequired} className="table-overflow-visible">
                                                <Table bordered responsive size="sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Resource</th>
                                                            <th>Type</th>
                                                            <th>Quantity</th>

                                                            {Boolean(job.contract.isProjectContract) && (
                                                                <>
                                                                    <th>Quantity Picked Up</th>
                                                                    <th>Rental Start</th>
                                                                    <th>Rental End</th>
                                                                </>
                                                            )}

                                                            <th style={{ width: '50px', textAlign: 'center' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!job?.resources?.length ? (
                                                            <tr>
                                                                <td colSpan={4} className="text-center">
                                                                    No resources assigned.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            job?.resources
                                                                .filter((r) => {
                                                                    if (r.isPlanningOnly) return false;
                                                                            
                                                                    if (
                                                                        r.jobResourceTypeId === ChargeTypeResourceTypes.Rental &&
                                                                                            !job.contract.isProjectContract
                                                                    ) return false;
                                                                            
                                                                    if (!r.setupChargeTypeId && !r.certificationId) return true;
                                                                            
                                                                    if (r.setupChargeTypeId > 0) {
                                                                        return r.isNew === true || this.resourceHasAnyAssignments(r);
                                                                    }
                                                                
                                                                    if (r.certificationId > 0) {
                                                                        return r.isNew === true || this.resourceHasAnyAssignments(r);
                                                                    }
                                                                            
                                                                    return false;
                                                                })
                                                                .map((resource) => {
                                                                    const dayKey = selectedDate?.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();
                                                                    const quantity = resource[dayKey] || 0;
                                                                    const dayKeyHasTimesheet = `${dayKey}HasTimesheet`;
                                                                    const isDisabled = resource[dayKeyHasTimesheet] || (resource.isPlanningOnly && !this.doesAfadAssignmentExistForDay(job, dayKey));

                                                                    return (
                                                                        <tr key={resource.id}>
                                                                            <td>{resource.resourceName}</td>
                                                                            <td>{resource.typeName}</td>

                                                                            {resource.jobResourceTypeId !== ChargeTypeResourceTypes.Rental && (
                                                                                <td>
                                                                                    <NumericInput
                                                                                        min={0}
                                                                                        step={1}
                                                                                        wholeNumber
                                                                                        className="text-right"
                                                                                        value={quantity}
                                                                                        onChange={(e) => this.handleQuantityChange(resource.id, e.target.value)}
                                                                                        disabled={
                                                                                            isDisabled ||
                                                                                            !edit.allowResources ||
                                                                                            resource?.setupChargeTypeId > 0
                                                                                        }
                                                                                        title={
                                                                                            resource[dayKeyHasTimesheet]
                                                                                                ? 'Approved Timesheet Exists'
                                                                                                : ''
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                            )}

                                                                            {job.contract.isProjectContract && (
                                                                                <>
                                                                                    {resource.jobResourceTypeId === ChargeTypeResourceTypes.Rental
                                                                                        ? (
                                                                                            <>
                                                                                                <td>
                                                                                                    <NumericInput
                                                                                                        min={0}
                                                                                                        step={1}
                                                                                                        wholeNumber
                                                                                                        className="text-right"
                                                                                                        value={resource.rentalDropoffQuantity}
                                                                                                        onChange={(e) => this.handleRentalQuantityChange(resource.id, 'rentalDropoffQuantity', e.target.value)}
                                                                                                        disabled={
                                                                                                            isDisabled ||
                                                                                                            !edit.allowResources ||
                                                                                                            resource?.setupChargeTypeId > 0
                                                                                                        }
                                                                                                        title={
                                                                                                            resource[dayKeyHasTimesheet]
                                                                                                                ? 'Approved Timesheet Exists'
                                                                                                                : ''
                                                                                                        }
                                                                                                    />
                                                                                                </td>

                                                                                                <td>
                                                                                                    <NumericInput
                                                                                                        min={0}
                                                                                                        step={1}
                                                                                                        wholeNumber
                                                                                                        className="text-right"
                                                                                                        value={resource.rentalPickupQuantity}
                                                                                                        onChange={(e) => this.handleRentalQuantityChange(resource.id, 'rentalPickupQuantity', e.target.value)}
                                                                                                        disabled={
                                                                                                            isDisabled ||
                                                                                                            !edit.allowResources ||
                                                                                                            resource?.setupChargeTypeId > 0
                                                                                                        }
                                                                                                        title={
                                                                                                            resource[dayKeyHasTimesheet]
                                                                                                                ? 'Approved Timesheet Exists'
                                                                                                                : ''
                                                                                                        }
                                                                                                        endAddon={(
                                                                                                            <>
                                                                                                                <IconButton
                                                                                                                    id={`pickup-remaining-${resource.id}`}
                                                                                                                    onClick={() => this.handlePickupRemainingEquipment(resource.id)}
                                                                                                                >
                                                                                                                    <FontAwesomeIcon
                                                                                                                        icon={faArrowRightFromBracket}
                                                                                                                    />
                                                                                                                </IconButton>

                                                                                                                <UncontrolledTooltip
                                                                                                                    target={`pickup-remaining-${resource.id}`}
                                                                                                                >
                                                                                                                    Pick up all remaining equipment
                                                                                                                </UncontrolledTooltip>
                                                                                                            </>
                                                                                                        )}
                                                                                                    />
                                                                                                </td>

                                                                                                <td>
                                                                                                    <DatePicker
                                                                                                        className="form-control"
                                                                                                        popperPlacement="top-end"
                                                                                                        name="rentalStartDate"
                                                                                                        selected={resource.rentalStartDate ? moment(resource.rentalStartDate).toDate() : ''}
                                                                                                        onChange={(date) => this.handleRentalDateChange(resource.id, 'rentalStartDate', date)}
                                                                                                        selectsStart
                                                                                                        showTimeInput={resource.rentalUnitTypeId === RentalUnitTypes.Hourly}
                                                                                                        timeInputLabel="Time: "
                                                                                                        timeFormat={resource.rentalUnitTypeId === RentalUnitTypes.Hourly ? 'p' : undefined}
                                                                                                        dateFormat={resource.rentalUnitTypeId === RentalUnitTypes.Hourly ? 'Pp' : undefined}
                                                                                                        startDate={new Date()}
                                                                                                        disabled={
                                                                                                            isDisabled ||
                                                                                                            !edit.allowResources ||
                                                                                                            resource?.setupChargeTypeId > 0 ||
                                                                                                            resource.rentalDropoffQuantity <= 0
                                                                                                        }
                                                                                                    />
                                                                                                </td>

                                                                                                <td>
                                                                                                    <DatePicker
                                                                                                        className="form-control"
                                                                                                        name="rentalEndDate"
                                                                                                        selected={resource.rentalEndDate ? moment(resource.rentalEndDate).toDate() : ''}
                                                                                                        onChange={(date) => this.handleRentalDateChange(resource.id, 'rentalEndDate', date)}
                                                                                                        selectsStart
                                                                                                        showTimeInput={resource.rentalUnitTypeId === RentalUnitTypes.Hourly}
                                                                                                        timeInputLabel="Time: "
                                                                                                        timeFormat={resource.rentalUnitTypeId === RentalUnitTypes.Hourly ? 'p' : undefined}
                                                                                                        dateFormat={resource.rentalUnitTypeId === RentalUnitTypes.Hourly ? 'Pp' : undefined}
                                                                                                        startDate={new Date()}
                                                                                                        disabled={
                                                                                                            isDisabled ||
                                                                                                            !edit.allowResources ||
                                                                                                            resource?.setupChargeTypeId > 0 ||
                                                                                                            resource.rentalPickupQuantity <= 0
                                                                                                        }
                                                                                                    />
                                                                                                </td>
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <td colSpan={3} />
                                                                                            </>
                                                                                        )}
                                                                                </>
                                                                            )}

                                                                            <td style={{ textAlign: 'center' }}>
                                                                                {resource?.setupChargeTypeId > 0 && (
                                                                                    <IconButton
                                                                                        title="Remove setup charge"
                                                                                        onClick={() =>
                                                                                            this.removeSetupCharge(resource)
                                                                                        }
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            icon={faTimes}
                                                                                            className="text-danger cursor-pointer"
                                                                                        />
                                                                                    </IconButton>
                                                                                )}

                                                                                {resource?.certificationId > 0 && (
                                                                                    <IconButton
                                                                                        title="Remove certification"
                                                                                        onClick={() =>
                                                                                            this.removeCertification(resource)
                                                                                        }
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            icon={faTimes}
                                                                                            className="text-danger cursor-pointer"
                                                                                        />
                                                                                    </IconButton>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                        )}
                                                    </tbody>

                                                    {job.contract.isProjectContract && (
                                                        <tfoot>
                                                            <td colSpan={4} />

                                                            <td align="right">
                                                                <Button
                                                                    color="link"
                                                                    type="button"
                                                                    size="sm"
                                                                    onClick={this.applySameRentalStartDateToAll}
                                                                >
                                                                    <FontAwesomeIcon icon={faPaste} className="mr-1" />
                                                                    Use same for all
                                                                </Button>
                                                            </td>

                                                            <td align="right">
                                                                <Button
                                                                    color="link"
                                                                    type="button"
                                                                    size="sm"
                                                                    onClick={this.applySameRentalEndDateToAll}
                                                                >
                                                                    <FontAwesomeIcon icon={faPaste} className="mr-1" />
                                                                    Use same for all
                                                                </Button>
                                                            </td>

                                                            <td></td>
                                                        </tfoot>
                                                    )}
                                                </Table>
                                            </div>
                                            {!isEmpty(tableRequired) && (
                                                <small className="text-danger">
                                                    {formState.errors.resources}
                                                </small>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </GroupedRow>
                                {this.isRequestedPersonnelSectionAvailable() && (
                                    <GroupedRow>
                                        <Col>
                                            <FormGroup className="mb-0">
                                                <FormLabel text="Requested Personnel" required />
                                                <div>
                                                    <Table bordered responsive size="sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Resource</th>
                                                                <th>Type</th>
                                                                <th>Assigned Days</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                job?.resources
                                                                    .filter((r) => r.isPlanningOnly)
                                                                    .map((resource) => {
                                                                        const dayKey = selectedDate?.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();
                                                                        return (
                                                                            <tr key={resource.id}>
                                                                                <td>{resource.resourceName}</td>
                                                                                <td>{resource.typeName}</td>
                                                                                <td>
                                                                                    <NumericInput
                                                                                        min="0"
                                                                                        value={resource[dayKey] || 0}
                                                                                        onChange={(e) => this.handleQuantityChange(resource.id, e.target.value)}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })
                                                            }
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </GroupedRow>
                                )}

                                {/* Setup Charges */}
                                {job?.contract?.setupChargesRequired && (
                                    <GroupedRow>
                                        <Col>
                                            <Button size="sm" color="primary" onClick={() => this.setState({ showAddSetupCharge: true })}>
                                                <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Setup Charge
                                            </Button>
                                            <small className="text-danger">
                                                {formState.errors.setupCharge}
                                            </small>
                                        </Col>
                                    </GroupedRow>
                                )}

                                {job?.contract?.certificationRequired && (
                                    <GroupedRow>
                                        <Col>
                                            <Button size="sm" color="primary" onClick={() => this.setState({ showAddCertification: true })}>
                                                <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Certification Requirement
                                            </Button>
                                        </Col>
                                    </GroupedRow>
                                )}

                                {/* Permit Fees */}
                                {job?.contract?.permitFeesApply && (
                                    <GroupedRow>
                                        <FormGroup>
                                            <div className="ml-3">
                                                <FormCheckbox
                                                    id="permitFeesApply"
                                                    name="permitFeesApply"
                                                    checked={!!job.permitFeesApply}
                                                    onChange={this.handlePermitFeesApplyChange}
                                                    labelText="Permit Fees Apply"
                                                />
                                            </div>
                                        </FormGroup>
                                        {job?.permitFeesApply && (
                                            <FormGroup>
                                                <div className="ml-5">
                                                    <Button size="sm" color="secondary" onClick={() => this.setState({ showAddPermitFees: true })}>
                                                        <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Permit Fees...
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        )}
                                    </GroupedRow>
                                )}

                                {/* Locations */}
                                <GroupedRow>
                                    <FormGroupColumn>
                                        <LocationSelector
                                            label="Meeting Location"
                                            type="meetingLocation"
                                            stateData={job}
                                            contract={job?.contract}
                                            usCounties={usCounties}
                                            addressFormRef={this.addressFormRef}
                                            setSelectedAddressType={(type) => this.setState({ selectedAddressType: type })}
                                            locationRequired={formState.errors.meetingLocation}
                                            isDisabled={!edit.allowMeetingLocation}
                                        />
                                    </FormGroupColumn>
                                    <FormGroupColumn>
                                        <LocationSelector
                                            label="Job Location"
                                            type="jobLocation"
                                            stateData={job}
                                            contract={job?.contract}
                                            usCounties={usCounties}
                                            addressFormRef={this.addressFormRef}
                                            setSelectedAddressType={(type) => this.setState({ selectedAddressType: type })}
                                            locationRequired={formState.errors.jobLocation}
                                            isDisabled={!edit.allowJobLocation}
                                        />
                                    </FormGroupColumn>
                                </GroupedRow>

                                {/* Notes */}
                                <GroupedRow>
                                    <Col>
                                        <Textarea
                                            label="Notes"
                                            name="job.notes"
                                            rows="5"
                                            maxLength={1000}
                                            value={job?.notes || ''}
                                            onChange={this.onChange}
                                            disabled={!edit.allowNotes}
                                        />
                                    </Col>
                                </GroupedRow>

                                {!!tenantSettings?.jobUploadsEnabled && (
                                    <GroupedRow>
                                        <Col>
                                            <FormGroup>
                                                <FormLabel
                                                    text={
                                                        <>
                                                            Attach Files
                                                            <small className="text-muted d-block mt-1">
                                                                Max 40MB | Supported: {supportedFiles}
                                                            </small>
                                                        </>
                                                    }
                                                />
                                                <FileUpload
                                                    id="jobUploader"
                                                    accept={supportedFiles}
                                                    maxNameLength={150}
                                                    url={ApiRoutes.job.postAttachments(
                                                        job.id || this.state.uploadSessionId
                                                    )}
                                                    onUploadComplete={() => this.getAttachments()}
                                                    files={job.uploads || []}
                                                    onDownload={this.onDownloadAttachment}
                                                    onDelete={this.onRemoveAttachment}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </GroupedRow>
                                )}
                                {/* Save Button */}

                                <div className="text-center mt-3">
                                    <Button color="primary" type="submit" disabled={formState.saving}>
                                        {formState.saving ? (
                                            <div className="d-flex align-items-center justify-content-center">
                                                <Spinner
                                                    size="sm"
                                                    className="saving-button-progress text-success mr-2"
                                                />
                                                <span className="ml-2">Saving</span>
                                            </div>
                                        ) : (
                                            job?.id ? 'Update Job' : 'Save Job'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </ModalBody>
                </Modal>

                {/* Address Form for Meeting/Job Location */}
                <AddressFormNew
                    ref={this.addressFormRef}
                    onSaveCallback={this.onSaveAddress}
                    isUnionWork={contract?.isUnionWork}
                    countryId={project?.countryId}
                    countryCode={project?.countryCode}
                    contractUsesPrevailingWage={contract?.prevailingWage}
                    contractId={contract?.id}
                    usCounties={usCounties}
                />

                <Modal isOpen={showAddSetupCharge}>
                    <ModalHeader>Add Setup Charge</ModalHeader>
                    <ModalBody>
                        <table className="table table-sm table-bordered table-striped payroll-table">
                            <thead className="text-muted">
                                <tr>
                                    <th style={{ width: '50px' }} />
                                    <th className="invoiceHourlyCharge">
                                        Charge Name
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {!unusedSetupChargeTypes?.length ? (
                                    <tr>
                                        <td className="text-center">
                                            No Setup Charges Available
                                        </td>
                                    </tr>
                                ) : (
                                    unusedSetupChargeTypes?.map(
                                        (charge,) => {
                                            const id = `${charge.id}|${uuid()}`;
                                            return (
                                                <tr key={id}>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <FormCheckbox
                                                            id={id}
                                                            name={id}
                                                            type="checkbox"
                                                            checked={charge.isNew == true}
                                                            onChange={(x) => {
                                                                charge.isNew = x.currentTarget.checked;
                                                                this.setState({ charge });
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="invoiceHourlyResource">
                                                        {charge.resourceName}
                                                    </td>
                                                </tr>
                                            );
                                        },
                                    )
                                )}
                            </tbody>
                        </table>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            onClick={this.onAddSetupCharge}
                        >
                            Done
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={showAddPermitFees}>
                    <ModalHeader>Add Permit Fees</ModalHeader>
                    <ModalBody>
                        <table className="table table-sm table-bordered table-striped payroll-table">
                            <thead className="text-muted">
                                <tr>
                                    <th style={{ width: '50px' }} />
                                    <th className="invoiceHourlyCharge">
                                        Charge Name
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {!unusedSetupChargeTypes?.length ? (
                                    <tr>
                                        <td className="text-center">
                                            No Permit Fees Available
                                        </td>
                                    </tr>
                                ) : (
                                    unusedPermitFees?.map(
                                        (charge) => (
                                            <tr key={`${charge.id}|${uuid()}`}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <FormCheckbox
                                                        id={charge.id}
                                                        name={charge.id}
                                                        type="checkbox"
                                                        checked={charge.isNew == true}
                                                        onChange={(x) => {
                                                            charge.isNew = x.currentTarget.checked;
                                                            this.setState({ charge });
                                                        }}
                                                    />
                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    {charge.resourceName}
                                                </td>
                                            </tr>
                                        ),
                                    )
                                )}
                            </tbody>
                        </table>

                    </ModalBody>

                    <ModalFooter>
                        <Button
                            color="primary"
                            onClick={this.onAddPermitFees}
                        >
                            Done
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={showAddCertification} toggle={() => this.setState({ showAddCertification: false })}>
                    <ModalHeader>Add Certification Requirement</ModalHeader>
                    <ModalBody>
                        <Table bordered responsive size="sm" className="payroll-table">
                            <thead className="text-muted">
                                <tr>
                                    <th style={{ width: 50 }} />
                                    <th>Certification Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!unusedCertifications.length ? (
                                    <tr>
                                        <td className="text-center" colSpan={2}>
                                            No Certification Available
                                        </td>
                                    </tr>
                                ) : (
                                    unusedCertifications.map((charge) => {
                                        const id = `${charge.id}|${uuid()}`;
                                        return (
                                            <tr key={id}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <FormCheckbox
                                                        id={id}
                                                        name={id}
                                                        type="checkbox"
                                                        checked={charge.isNew == true}
                                                        onChange={(x) => {
                                                            charge.isNew = x.currentTarget.checked;
                                                            this.setState({ charge });
                                                        }}
                                                    />
                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    <label htmlFor={id}>{charge.resourceName}</label>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={this.onAddCertification}>
                            Done
                        </Button>
                    </ModalFooter>
                </Modal>
            </React.Fragment>
        );
    }
}

export default withRouter(JobForm);
